import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney, localDate } from '@/platform/lib/utils';
import {
    Button,
    Checkbox,
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import {
    DocumentCheckIcon,
    QuestionMarkCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import { Fragment, useState } from 'react';
import DataTypes from './components/DataTypes';
import EmployeeCard from './components/EmployeeCard';
import EmploymentPeriodsTable from './components/EmploymentPeriodsTable';
import Navigation from './components/Navigation';

const taxRuleDescriptions = {
    all: 'De premie die betaald wordt (hoog of laag) verschilt van wat we berekenen met behulp van de bovenstaande belastingregels',
    under_21:
        'De werknemer is jonger dan 21 jaar en krijgt een vergoeding van maximaal 48 uur per periode van 4 weken of maximaal 52 uur per maandperiode.',
    bbl: 'Het betreft een beroepsopleiding en de werknemer heeft zowel een praktijkovereenkomst als een arbeidsovereenkomst. De praktijkovereenkomst moet schriftelijk worden vastgelegd en de datum vermelden waarop deze is overeengekomen. Dit wordt aangegeven door de code aard arbeidsverhouding 83.',
    uwv_benefit:
        'De werknemer krijgt namens het UWV een WW-, ZW-, WIA-, WAO- of WAZO-uitkering, of de uitkering wordt door de onderneming als eigenrisicodrager betaald. Dit wordt aangegeven door de soort inkomstenverhouding-code gerelateerd aan de soort uitkering.',
    contract: (
        <>
            De arbeidsovereenkomst voldoet aan de 3 belangrijkste voorwaarden:
            <ul className='mt-1 list-disc pl-4'>
                <li>
                    De arbeidsovereenkomst is voor onbepaalde tijd aangegaan.
                </li>
                <li>De arbeidsovereenkomst wordt schriftelijk vastgelegd.</li>
                <li>De arbeidsovereenkomst is géén oproepcontract.</li>
            </ul>
        </>
    ),
};

const businessRuleDescriptions = {
    should_be_written:
        'De enige voorwaarde die niet wordt vervuld, is dat het contract niet schriftelijk is vastgelegd (dat wil zeggen, het is permanent en niet oproepbaar)',
    high_earners:
        'De werknemer verdient meer dan het maximale SV-loon, maar het bedrijf betaalt de hoge premie voor hen',
    other_high_earners:
        'Werknemer wordt getoond onder Overige, als deze tenminste 1 periode de hoge premie heeft afgedragen, en niet valt onder de andere signalen',
};

const BusinessRulesView = () => {
    const { auth, employments, business_rules_enum, types } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['employments']);

    const [selected, setSelected] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSelect = employmentId => {
        setSelected(prev => {
            if (prev?.includes(employmentId)) {
                return [...prev.filter(e => e != employmentId)];
            }

            return [...prev, employmentId];
        });
    };

    const getPotentialSavings = employment => {
        if (!filters?.year) {
            return employment.potential_savings;
        }

        if (!(filters?.year in employment.employment_periods.data)) {
            return 0;
        }

        return employment.employment_periods.data[filters?.year]?.totals
            ?.potential_savings;
    };

    const handleRowSelected = (event, employmentId) => {
        if (
            !event.currentTarget.contains(event.target) ||
            event.target.closest(
                'button, a, input, label, select, textarea, [role="checkbox"]'
            )
        ) {
            return;
        }

        setSelectedRow(prev => (prev === employmentId ? null : employmentId));
    };

    const handleContractCheck = (employmentIds, skip = false) => {
        (setIsProcessing(true),
            router.post(
                `/customers/${auth?.user?.selected_customer_id}/services/ww-check/contract-check`,
                {
                    employmentIds,
                    status: skip ? 0 : 1,
                },
                {
                    preserveScroll: true,
                    only: [
                        'employments',
                        'business_rules',
                        'contract_checks',
                        'savings',
                    ],
                    onSuccess: () => setSelected([]),
                    onFinish: () => setIsProcessing(false),
                }
            ));
    };

    let numberOfColumns = 8;

    if (auth?.permissions?.includes('create ww contract check')) {
        numberOfColumns += 1;
    }

    if (filters?.filter) {
        numberOfColumns += 1;
    }

    const getHelpText = () => {
        if (!filters?.filter) {
            return {
                title: 'Alle',
                content: 'Alle medewerkers die voldoen aan de Business rules',
            };
        }

        const rule = business_rules_enum?.find(r => r.id === filters.filter);

        if (filters.filter === 'tax_rules' && filters?.type) {
            return {
                title: types?.find(t => t.id === filters.type)?.name,
                content: taxRuleDescriptions[filters.type],
            };
        }

        if (filters.filter === 'tax_rules') {
            return { title: rule?.name, content: taxRuleDescriptions.all };
        }

        if (filters.filter === 'long_employments') {
            return {
                title: rule?.name,
                content: `De werknemers zijn al langer dan ${filters?.months} maanden in dienst, maar hebben nog steeds een contract voor bepaalde tijd`,
            };
        }

        return {
            title: rule?.name,
            content: businessRuleDescriptions[filters.filter],
        };
    };

    const helpText = getHelpText();

    return (
        <Container fluid>
            <Navigation reload={['employments']} />

            <Segment>
                <SegmentHeader
                    header={`Business Rules (${employments?.meta?.total || 0})`}
                >
                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionMarkCircleIcon className='size-5' />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className='font-semibold'>{helpText.title}</p>
                            <div className='font-normal text-gray-200'>
                                {helpText.content}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['employments']}>
                        <FilterBarDropdown
                            clearable
                            label={`Regel (${business_rules_enum?.find(r => r.id == filters?.filter)?.name || 'Alle'})`}
                            value={filters?.filter || ''}
                            options={business_rules_enum || []}
                            onChange={value =>
                                updateFilters(
                                    'filter',
                                    value,
                                    value !== 'tax_rules' ? { type: null } : {}
                                )
                            }
                        />
                        {filters?.filter === 'tax_rules' && (
                            <FilterBarDropdown
                                clearable
                                label={`Type (${types?.find(t => t.id == filters?.type)?.name || 'Alle'})`}
                                value={filters?.type || ''}
                                options={types || []}
                                onChange={value => updateFilters('type', value)}
                            />
                        )}
                    </SearchBarFilter>

                    {auth?.permissions?.includes(
                        'create ww contract check'
                    ) && (
                        <div className='mb-4 flex items-center justify-end gap-2'>
                            <Button
                                variant='danger'
                                disabled={selected?.length === 0}
                                loading={isProcessing}
                                onClick={() =>
                                    handleContractCheck(selected, true)
                                }
                            >
                                <XCircleIcon className='size-5' />
                                Verbergen uit lijst
                            </Button>
                            <Button
                                variant='primary'
                                disabled={selected?.length === 0}
                                loading={isProcessing}
                                onClick={() => handleContractCheck(selected)}
                            >
                                <DocumentCheckIcon className='size-5' />
                                Contract aanvragen
                            </Button>
                        </div>
                    )}

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                {auth?.permissions?.includes(
                                    'create ww contract check'
                                ) && (
                                    <TableHeaderCell textAlign='center'>
                                        <Checkbox
                                            checked={
                                                selected.length ===
                                                employments?.data?.length
                                            }
                                            onChange={checked =>
                                                setSelected(
                                                    checked
                                                        ? employments?.data?.map(
                                                              e => e.id
                                                          )
                                                        : []
                                                )
                                            }
                                        />
                                    </TableHeaderCell>
                                )}
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('starts_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'starts_at')
                                    }
                                >
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell collapsing>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <TableHeaderCell
                                            textAlign='center'
                                            collapsing
                                        >
                                            SrtIV
                                        </TableHeaderCell>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Soort Inkomstenverhouding
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <TableHeaderCell
                                            textAlign='center'
                                            collapsing
                                        >
                                            CdAard
                                        </TableHeaderCell>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Code aard arbeidsverhouding
                                    </TooltipContent>
                                </Tooltip>
                                <TableHeaderCell
                                    textAlign='right'
                                    sorted={isSorted('premieAwfSum')}
                                    onClick={() =>
                                        updateFilters('sort', 'premieAwfSum')
                                    }
                                >
                                    Premie
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Percentage
                                </TableHeaderCell>
                                {!!filters?.filter && (
                                    <TableHeaderCell textAlign='right'>
                                        Mog. Besparing
                                    </TableHeaderCell>
                                )}
                                <TableHeaderCell textAlign='right'>
                                    Acties
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {employments?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={numberOfColumns} />
                            )}

                            {employments?.data?.map(employment => (
                                <Fragment key={employment.id}>
                                    <TableRow
                                        onClick={event =>
                                            handleRowSelected(
                                                event,
                                                employment.id
                                            )
                                        }
                                    >
                                        {auth?.permissions?.includes(
                                            'create ww contract check'
                                        ) && (
                                            <TableCell textAlign='center'>
                                                <Checkbox
                                                    checked={selected?.includes(
                                                        employment.id
                                                    )}
                                                    onChange={() =>
                                                        handleSelect(
                                                            employment.id
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <EmployeeCard
                                                employment={employment}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {localDate(employment?.starts_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span>
                                                        {localDate(
                                                            employment.ends_at
                                                        ) || '-'}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {employment.duration}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell
                                            textAlign='center'
                                            collapsing
                                        >
                                            <DataTypes
                                                data={
                                                    employment?.income_relationship_types ||
                                                    []
                                                }
                                                type='income_relationship_type'
                                            />
                                        </TableCell>
                                        <TableCell
                                            textAlign='center'
                                            collapsing
                                        >
                                            <DataTypes
                                                data={
                                                    employment?.nature_of_employments ||
                                                    []
                                                }
                                                type='nature_of_employment'
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ul className='flex flex-col items-end gap-2'>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <li className='font-semibold'>
                                                            {formatMoney(
                                                                employment.premie_awf_sum ||
                                                                    0
                                                            )}
                                                        </li>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Premie totaal
                                                    </TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <li className='text-gray-500'>
                                                            {formatMoney(
                                                                employment.premieloon_awf_sum ||
                                                                    0
                                                            )}
                                                        </li>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Premieloon totaal
                                                    </TooltipContent>
                                                </Tooltip>
                                            </ul>
                                        </TableCell>
                                        <TableCell>
                                            <ul className='flex flex-col items-end gap-2'>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <li className='font-semibold'>{`${employment.premie_awf_percentage} %`}</li>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Percentage van het
                                                        premie totaal
                                                    </TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <li className='text-gray-500'>
                                                            {
                                                                employment.premie_awf_type
                                                            }
                                                        </li>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        Indicatie van welke
                                                        premie veld is gebruikt
                                                        in de aangifte
                                                        loonbelasting
                                                    </TooltipContent>
                                                </Tooltip>
                                            </ul>
                                        </TableCell>
                                        {!!filters?.filter && (
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    getPotentialSavings(
                                                        employment
                                                    )
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {auth?.permissions?.includes(
                                                'create ww contract check'
                                            ) && (
                                                <div className='flex items-center justify-end gap-2'>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant='danger'
                                                                loading={
                                                                    isProcessing
                                                                }
                                                                onClick={() =>
                                                                    handleContractCheck(
                                                                        [
                                                                            employment.id,
                                                                        ],
                                                                        true
                                                                    )
                                                                }
                                                            >
                                                                <XCircleIcon className='size-4' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            Verbergen uit lijst
                                                        </TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant='primary'
                                                                loading={
                                                                    isProcessing
                                                                }
                                                                onClick={() =>
                                                                    handleContractCheck(
                                                                        [
                                                                            employment.id,
                                                                        ]
                                                                    )
                                                                }
                                                            >
                                                                <DocumentCheckIcon className='size-4' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            Contract aanvragen
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {selectedRow === employment.id && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={numberOfColumns}
                                            >
                                                <EmploymentPeriodsTable
                                                    year={filters?.year}
                                                    data={
                                                        employment.employment_periods
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={employments?.meta}
                        only={['employments']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default BusinessRulesView;
